/**
 * AuthProvider - React Context for authentication state
 * Manages global auth state and provides auth operations
 */

import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth.service';
import type { AuthContextValue, AuthState } from '../../types/auth.types';

// Create context with undefined default
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * AuthProvider component
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();

  // Auth state
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    hasCheckedSession: false,
    error: null,
  });

  /**
   * Update state helper
   */
  const updateState = useCallback((updates: Partial<AuthState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    updateState({ error: null });
  }, [updateState]);

  /**
   * Login function
   */
  const login = useCallback(
    async (email: string, password: string, rememberMe = false): Promise<void> => {
      console.log('[AUTH] Login attempt started', { email, rememberMe });
      try {
        updateState({ isLoading: true, error: null });

        console.log('[AUTH] Calling authService.login...');
        const response = await authService.login({
          email,
          password,
          rememberMe,
        });

        console.log('[AUTH] Login successful', { user: response.user });

        // Ensure role is set
        const user = response.user ? {
          ...response.user,
          role: response.user.role || 'customer' as 'admin' | 'customer'
        } : null;

        updateState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });

        // Navigate based on role
        if (user?.role === 'admin') {
          navigate('/customers');
        } else {
          navigate('/customer/dashboard');
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Login failed';
        console.error('[AUTH] Login failed', { error, errorMessage });

        // Display error visually
        alert(`Login Failed: ${errorMessage}`);

        updateState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: errorMessage,
        });
        throw error;
      }
    },
    [updateState, navigate]
  );

  /**
   * Logout function
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      updateState({ isLoading: true, error: null });

      await authService.logout();

      updateState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });

      // Navigate to login
      navigate('/login');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Logout failed';
      updateState({
        isLoading: false,
        error: errorMessage,
      });
    }
  }, [updateState, navigate]);

  /**
   * Check session status
   * NOTE: Only called on initial mount to restore session from cookie
   * Does NOT logout on failure to prevent React Strict Mode issues
   */
  const checkSession = useCallback(async (): Promise<void> => {
    try {
      updateState({ isLoading: true });

      const response = await authService.checkSession();

      // Ensure role is set, default to 'customer' if not provided
      const user = response.user ? {
        ...response.user,
        role: response.user.role || 'customer' as 'admin' | 'customer'
      } : null;

      updateState({
        user,
        isAuthenticated: response.authenticated,
        isLoading: false,
        hasCheckedSession: true,
        error: null,
      });
    } catch (error) {
      // Don't immediately logout on session check failure
      // This prevents logout on React Strict Mode double-invocation
      // and on component remounting during navigation
      console.log('[AUTH] Session check failed (not logging out)', error);

      updateState({
        isLoading: false,
        hasCheckedSession: true,
        error: null, // Don't show error for failed session check
      });
      // Note: We do NOT set isAuthenticated: false here
      // Only explicit logout or failed refresh should clear authentication
    }
  }, [updateState]);

  /**
   * Refresh session with retry logic
   */
  const refreshSession = useCallback(async (retryCount = 0): Promise<void> => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 2000; // 2 seconds

    try {
      console.log('[AUTH] Refreshing session...', retryCount > 0 ? `(Retry ${retryCount}/${MAX_RETRIES})` : '');
      const response = await authService.refreshSession();

      console.log('[AUTH] Session refreshed successfully', { authenticated: response.authenticated });

      updateState({
        user: response.user,
        isAuthenticated: response.authenticated,
        error: null,
      });
    } catch (error) {
      console.warn('[AUTH] Session refresh failed', error);

      // Retry logic - if we haven't exhausted retries, try again
      if (retryCount < MAX_RETRIES) {
        console.log(`[AUTH] Retrying refresh (${retryCount + 1}/${MAX_RETRIES}) in ${RETRY_DELAY * (retryCount + 1)}ms...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
        return refreshSession(retryCount + 1);
      }

      // All retries exhausted - check if we're still authenticated
      // This prevents logout on temporary network issues
      try {
        const sessionCheck = await authService.checkSession();
        if (sessionCheck.authenticated) {
          console.log('[AUTH] Session still valid despite refresh failure');
          updateState({
            user: sessionCheck.user,
            isAuthenticated: true,
            error: null,
          });
          return;
        }
      } catch (checkError) {
        console.error('[AUTH] Session check also failed after retries', checkError);
      }

      // Both refresh and check failed after retries - logout user
      console.log('[AUTH] Logging out - session confirmed invalid after retries');
      updateState({
        user: null,
        isAuthenticated: false,
        error: null,
      });

      // Only navigate to login if not already there
      if (window.location.pathname !== '/login') {
        navigate('/login');
      }
    }
  }, [updateState, navigate]);

  /**
   * Initialize auth state on mount
   * Uses a ref to prevent React Strict Mode double-invocation
   */
  const hasCheckedSession = React.useRef(false);

  useEffect(() => {
    // Only check session once on initial mount
    // Prevents React Strict Mode from causing double logout
    if (!hasCheckedSession.current) {
      hasCheckedSession.current = true;
      checkSession();
    }
  }, [checkSession]);

  /**
   * Setup session refresh interval
   */
  useEffect(() => {
    if (!state.isAuthenticated) {
      console.log('[AUTH] Not authenticated, skipping refresh interval setup');
      return;
    }

    console.log('[AUTH] Setting up session refresh interval (6 hours)');

    // Refresh session every 6 hours (session expires in 7 days)
    // 6-hour interval reduces server load and network error probability
    const refreshInterval = setInterval(() => {
      console.log('[AUTH] Auto-refreshing session (6-hour interval)');
      refreshSession();
    }, 6 * 60 * 60 * 1000); // 6 hours

    return () => {
      console.log('[AUTH] Clearing session refresh interval');
      clearInterval(refreshInterval);
    };
  }, [state.isAuthenticated, refreshSession]);

  // Context value
  const value: AuthContextValue = {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    hasCheckedSession: state.hasCheckedSession,
    error: state.error,
    login,
    logout,
    checkSession,
    refreshSession,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
